import styled from "@emotion/styled";
import React, { useState } from "react";
import { useLang, useConvert, useYield, useArea, useLength } from "@src/hooks/useLang";
import { roundLengthForDisplay } from "@src/utils/convert";
import { useStores } from "@stores";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row } from "@src/components/Flex";
import Button from "@components/Button";
import { useDeviceCalibrationScreenVM } from "@screens/DeviceCalibrationScreen/DeviceCalibrationScreenVM";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";
import { ReactComponent as TrashIcon } from "@assets/icons/trash.svg";
import moment from "moment";
import RemoveConfirmDialog from "@screens/DeviceCalibrationScreen/dialogs/RemoveConfirmDialog";
import { WeightUnit } from "@services/demosService";
import BN from "@src/utils/BN";
import Tooltip from "@components/Tooltip";
import { ReactComponent as InfoIcon } from "@assets/icons/info.svg";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import {
  CalibrationStatusKind,
  calibrationErrorText,
  calibrationPlaceLabel,
  calibrationRecorderLabel,
  calibrationStatusKind,
  calibrationWidthMeters,
  formatCoefficient,
  isMobileCalibration,
  phoneCoefficientOf,
} from "@src/utils/calibrationDisplay";

/**
 * The calibration's own yield is the weighed mass over the area the machine
 * swept during the calibration window, NET OF HEADLAND TURNS (backend
 * calibrationComputeService: turns tighter than min_turn_radius_m drop out).
 * Analytics counts those turns, so its area is a little larger and its yield a
 * little lower for the same window — decided 2026-10-08 to label the
 * difference rather than run the turn gate on every dashboard query.
 */
const YIELD_HINT =
  "This is an estimate for this calibration only. Its area leaves out headland turns, which the yield in Analytics includes, so the two can differ slightly.";

interface IProps { }

const Root = styled.div`
  border-radius: 4px;
  overflow: scroll;
  width: 100%;
  box-sizing: border-box;
  max-height: 100vh;
  padding: 32px 24px;

  /* Phone: this is the whole page, not a scrolling pane beside a sidebar, so
     it must not trap the scroll in a 100vh box of its own — the browser's own
     scroll (and its address-bar collapse) has to own it. */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    overflow: visible;
    max-height: none;
    padding: 20px 12px 32px;
  }
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  border: 1px solid #ebeeef;
  th,
  td {
    height: 48px;
    border: 1px solid #ebeeef;
    text-align: left;
    box-sizing: border-box;
    padding-left: 12px;
  }

  .actions {
    position: absolute;
    top: 0;
    bottom: 0;
    padding-top: 14px;
    right: 14px;
    transition: 0.4s;
    opacity: 0;
    max-width: fit-content;
    cursor: pointer;
    z-index: 10;
  }

  tbody > tr {
    cursor: pointer;
    height: 44px;
    position: relative;
    transition: 0.4s;
    :hover {
      & > td > div {
        color: #417900;
      }
      background: #f8f8f8;
      .actions {
        opacity: 1;
      }
    }
  }
`;

/* Full-width on a phone: "Calibrate" is the only action on the screen and a
   fit-content button pinned right is a small target next to the edge. */
const ActionRow = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    button {
      width: 100%;
    }
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Card = styled.div`
  border: 1px solid #ebeeef;
  border-radius: 8px;
  padding: 14px;
  background: #fff;
  cursor: pointer;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

/* 44px keeps the trash icon at the minimum comfortable touch target — the
   desktop table reveals it on row hover, which a phone has no equivalent of,
   so here it is permanently visible instead. */
const DeleteButton = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin: -10px -10px 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
`;

/* Two columns of label/value. Labels are the same strings as the table
   headers, so the phone view stays translated with no new keys. */
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
`;

const CardCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CardLabel = styled.span`
  font-family: Roboto, sans-serif;
  font-size: 11px;
  color: #899093;
`;

const CardValue = styled.span`
  font-family: Roboto, sans-serif;
  font-size: 14px;
  color: #494c5b;
  word-break: break-word;
`;

/* Same look as the "Mobile" badge on loads in Weight tracking. */
const SourceBadge = styled.span`
  display: inline-block;
  margin-right: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #eef6e3;
  color: #68ad18;
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: nowrap;
`;

/* One quiet line under a cell's main value: who recorded a driver-app run,
   the phone's own coefficient, or why the computation failed. */
const SubLine = styled.div<{ tone?: "muted" | "error" }>`
  font-family: Roboto, sans-serif;
  font-size: 11px;
  line-height: 16px;
  color: ${({ tone }) => (tone === "error" ? "#c62828" : "#899093")};
`;

/* The desktop cell is narrow: one line, ellipsis, full text in the tooltip.
   The phone card has room, so the error wraps there instead. */
const ErrorLine = styled(SubLine)`
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeviceCalibrationTable: React.FC<IProps> = () => {
  const lang = useLang();
  const vm = useDeviceCalibrationScreenVM();
  const isMobile = useIsMobile();
  const [removeConfirmId, setRemoveConfirmId] = useState<string | null>();
  const { convertWeight, convertYield, convertArea, convertLength } = useConvert();
  const locYield = useYield();
  const locArea = useArea();
  const locLength = useLength();
  const { settingsStore } = useStores();
  const units = settingsStore.units;
  const t = (lang as any).devices ?? {};

  const handleRemove = () => {
    if (removeConfirmId != null) {
      vm.deleteCalibration(removeConfirmId)
        .then(() => setRemoveConfirmId(null))
        // calibrationService is raw fetch: nothing else reports the failure.
        .catch((e) => toast.error(e?.message ?? "Failed to delete calibration"));
    }
  };

  const yieldTitle = t.calTableYieldTitle ?? "Estimated yield";
  const yieldHint = t.calTableYieldHint ?? YIELD_HINT;
  const coefficientTitle = t.calTableCoefficientTitle ?? "Coefficient";
  const an = (lang as any).analytics ?? {};
  const mobileLabel = an.sourceMobile ?? "Mobile";
  const mobileHint = an.sourceMobileHint ?? "Recorded in the driver app";
  const recordedByWord = an.recordedBy ?? "Recorded by";
  const statusLabels: Record<Exclude<CalibrationStatusKind, "completed">, string> = {
    computing: t.calStatusComputing ?? "Computing…",
    waiting: t.calStatusWaiting ?? "Waiting for machine data",
    pending: t.calStatusPending ?? "Pending",
    failed: t.calStatusFailed ?? "Failed",
  };
  const waitingHint =
    t.calStatusWaitingHint ??
    "Recorded in the driver app. The coefficient is calculated on the server once the machine's data for this run arrives — usually within half an hour.";
  const phoneLabel = (value: string) =>
    (t.calPhoneCoefficient ?? "Phone: {value}").replace("{value}", value);
  const phoneHint =
    t.calPhoneCoefficientHint ??
    "The coefficient the phone calculated itself, kept for reference only. The server calculates the one that is used.";
  const widthFromMachineHint =
    t.calWidthFromMachineHint ??
    "Not entered — the machine's header width at that time is used";

  /**
   * One prepared row, so the table and the card list stay in step — they render
   * the same seven figures and only the layout differs.
   */
  const rows = vm.calibrations.map((data) => {
    // Backend sends kg/ha; convertYield expects centner/ha.
    const yieldKgHa = data.estimated_yield?.value ?? null;
    const areaHa = data.area?.value ?? null;
    const widthMeters = calibrationWidthMeters(data);
    const status = calibrationStatusKind(data);
    const recorder = calibrationRecorderLabel(data);
    const phoneCoefficient = phoneCoefficientOf(data);
    const error = status === "failed" ? calibrationErrorText(data) : null;
    return {
      id: data.id,
      // "—" for a driver-app run the server matched to no field.
      crop: calibrationPlaceLabel(data),
      isMobile: isMobileCalibration(data),
      recordedBy: recorder != null ? `${recordedByWord} ${recorder}` : null,
      recordedByTitle: data.recordedBy?.email ?? undefined,
      start: moment(data.start).format("DD.MM.YYYY HH:mm"),
      end: moment(data.stop).format("DD.MM.YYYY HH:mm"),
      weight: `${new BN(
        convertWeight(data.weight.value, WeightUnit.Kilogram)
      ).toFormat(2)} ${units.weight}`,
      // Driver-app runs carry no width of their own: the machine's width
      // timeline applies, which the backend lists as 0 — show a dash, not "0".
      width:
        widthMeters != null
          ? `${roundLengthForDisplay(
              convertLength(widthMeters, data.reaper_width.unit)
            )}${locLength()}`
          : "—",
      widthTitle: widthMeters != null ? undefined : widthFromMachineHint,
      hasYield: yieldKgHa != null,
      yieldLabel:
        yieldKgHa != null
          ? `${new BN(convertYield(yieldKgHa / 100)).toFormat(2)} ${locYield()}`
          : "—",
      areaLabel:
        areaHa != null
          ? `${t.calTableAreaHint ?? "Harvested area, net of headland turns"}: ${new BN(
              convertArea(areaHa)
            ).toFormat(2)} ${locArea()}`
          : undefined,
      hasCoefficient: status === "completed",
      coefficient:
        status === "completed" && data.coefficient != null
          ? formatCoefficient(data.coefficient)
          : statusLabels[status === "completed" ? "pending" : status],
      coefficientTitle:
        status === "waiting" ? waitingHint : status === "failed" ? error ?? undefined : undefined,
      isFailed: status === "failed",
      error,
      phoneCoefficient:
        phoneCoefficient != null ? phoneLabel(formatCoefficient(phoneCoefficient)) : null,
      select: () => {
        vm.setEditCalibrationId(data.id);
        vm.setDialogVisible(true);
      },
    };
  });

  const yieldHeading = (
    <Row alignItems="center" style={{ gap: 6 }}>
      <Text color="#494C5B" type="heading" size={14}>
        {yieldTitle}
      </Text>
      <Tooltip
        // Hover for mouse, click for touch — an info button that only
        // reacts to hover is unreachable on a phone.
        config={{ trigger: ["hover", "click"], interactive: true, placement: "top" }}
        content={
          <Text size={12} style={{ maxWidth: 320, display: "block" }}>
            {yieldHint}
          </Text>
        }
      >
        <InfoIcon width={14} height={14} style={{ display: "block" }} />
      </Tooltip>
    </Row>
  );

  return (
    <Root>
      <Text size={22} color="#494C5B" type="heading">
        {lang.devices.calibrationTitle}
      </Text>
      <SizedBox height={24} />
      <ActionRow>
        <Button
          style={{ width: "fit-content" }}
          onClick={() => vm.setDialogVisible(true)}
        >
          {lang.devices.calibrateButton}
        </Button>
      </ActionRow>
      <SizedBox height={16} />
      {/*
        Seven columns do not survive a 375px viewport: the table either scrolls
        sideways with the crop name off-screen or crushes every date onto three
        lines. Same data, stacked into a card per calibration instead.
      */}
      {isMobile ? (
        <CardList>
          {rows.map((row) => (
            <Card key={row.id} onClick={row.select}>
              <CardHeader>
                <div>
                  <Text size={16} color="#494C5B" type="heading" fitContent>
                    {row.crop}
                  </Text>
                  {(row.isMobile || row.recordedBy) && (
                    <SubLine title={row.recordedByTitle}>
                      {row.isMobile && (
                        <SourceBadge title={mobileHint}>{mobileLabel}</SourceBadge>
                      )}
                      {row.recordedBy}
                    </SubLine>
                  )}
                  <SizedBox height={2} />
                  <Text
                    size={12}
                    color={
                      row.hasCoefficient ? "#417900" : row.isFailed ? "#c62828" : "#899093"
                    }
                    fitContent
                    title={row.coefficientTitle}
                  >
                    {`${coefficientTitle}: ${row.coefficient}`}
                  </Text>
                  {row.phoneCoefficient && (
                    <SubLine title={phoneHint}>{row.phoneCoefficient}</SubLine>
                  )}
                  {row.error && <SubLine tone="error">{row.error}</SubLine>}
                </div>
                <DeleteButton
                  type="button"
                  aria-label={lang.devices.deleteCalibrationDelButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    setRemoveConfirmId(row.id);
                  }}
                >
                  <TrashIcon />
                </DeleteButton>
              </CardHeader>
              <CardGrid>
                <CardCell>
                  <CardLabel>{lang.devices.calTableStartTitle}</CardLabel>
                  <CardValue>{row.start}</CardValue>
                </CardCell>
                <CardCell>
                  <CardLabel>{lang.devices.calTableStopTitle}</CardLabel>
                  <CardValue>{row.end}</CardValue>
                </CardCell>
                <CardCell>
                  <CardLabel>{lang.devices.calTableCollectTitle}</CardLabel>
                  <CardValue>{row.weight}</CardValue>
                </CardCell>
                <CardCell title={row.widthTitle}>
                  <CardLabel>
                    {lang.devices.calTableHeaderTitle} ({locLength()})
                  </CardLabel>
                  <CardValue>{row.width}</CardValue>
                </CardCell>
                <CardCell style={{ gridColumn: "1 / -1" }} title={row.areaLabel}>
                  <CardLabel>{yieldTitle}</CardLabel>
                  <CardValue style={{ color: row.hasYield ? undefined : "#899093" }}>
                    {row.yieldLabel}
                  </CardValue>
                </CardCell>
              </CardGrid>
            </Card>
          ))}
        </CardList>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>
                <Text color="#494C5B" type="heading" size={14}>
                  {lang.devices.calTableCropTitle}
                </Text>
              </th>
              <th>
                <Text color="#494C5B" type="heading" size={14}>
                  {lang.devices.calTableCollectTitle}
                </Text>
              </th>
              <th>
                <Text color="#494C5B" type="heading" size={14}>
                  {lang.devices.calTableStartTitle}
                </Text>
              </th>
              <th>
                <Text color="#494C5B" type="heading" size={14}>
                  {lang.devices.calTableStopTitle}
                </Text>
              </th>
              <th>
                <Text color="#494C5B" type="heading" size={14}>
                  {lang.devices.calTableHeaderTitle} ({locLength()})
                </Text>
              </th>
              <th>{yieldHeading}</th>
              <th>
                <Text color="#494C5B" type="heading" size={14}>
                  {coefficientTitle}
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} onClick={row.select}>
                <td>
                  <Text color="#494C5B" size={14}>
                    {row.crop}
                  </Text>
                  {(row.isMobile || row.recordedBy) && (
                    <SubLine title={row.recordedByTitle}>
                      {row.isMobile && (
                        <SourceBadge title={mobileHint}>{mobileLabel}</SourceBadge>
                      )}
                      {row.recordedBy}
                    </SubLine>
                  )}
                </td>
                <td>
                  <Text color="#494C5B" size={14}>
                    {row.weight}
                  </Text>
                </td>
                <td>
                  <Text color="#494C5B" size={14}>
                    {row.start}
                  </Text>
                </td>
                <td>
                  <Text color="#494C5B" size={14}>
                    {row.end}
                  </Text>
                </td>
                <td title={row.widthTitle}>
                  <Text color={row.widthTitle ? "#899093" : "#494C5B"} size={14}>
                    {row.width}
                  </Text>
                </td>
                <td title={row.areaLabel}>
                  <Text color={row.hasYield ? "#494C5B" : "#899093"} size={14}>
                    {row.yieldLabel}
                  </Text>
                </td>
                <td title={row.coefficientTitle}>
                  <Text
                    color={
                      row.hasCoefficient ? "#417900" : row.isFailed ? "#c62828" : "#899093"
                    }
                    size={14}
                  >
                    {row.coefficient}
                  </Text>
                  {row.phoneCoefficient && (
                    <SubLine title={phoneHint}>{row.phoneCoefficient}</SubLine>
                  )}
                  {row.error && <ErrorLine tone="error">{row.error}</ErrorLine>}
                  <div className="actions">
                    <TrashIcon
                      onClick={(e) => {
                        e.stopPropagation();
                        setRemoveConfirmId(row.id);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {/* Was nested inside <table>, which is not valid table content — React
          renders it, but the browser hoists it out of the table on parse. */}
      <RemoveConfirmDialog
        visible={removeConfirmId != null}
        onClose={() => setRemoveConfirmId(null)}
        onSubmit={handleRemove}
      />
    </Root>
  );
};

export default observer(DeviceCalibrationTable);
